import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LineChart, SlidersHorizontal, Table2 } from 'lucide-react';

import { forecastService } from '@/services/demandPlanning/forecast.service';

import { ChartLegendHelp } from '../components/ChartLegendHelp';
import { DemandPlanningGuide } from '../components/DemandPlanningGuide';
import { ForecastChart } from '../components/ForecastChart';
import { ForecastInboxActionModal } from '../components/ForecastInboxActionModal';
import { ForecastProductTable } from '../components/ForecastProductTable';
import { ForecastWorkInbox } from '../components/ForecastWorkInbox';
import { HelpTip, LabelWithHelp } from '../components/HelpTip';
import { SectionCard } from '../components/SectionCard';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import {
  EMPTY_ENTITY_SCOPE_FILTERS,
  entityScopeFiltersKey,
  type EntityScopeFilters,
} from '../components/ConsensusScopeFilters';
import { EntityScopeFilterBar } from '../components/EntityScopeFilterBar';
import { useScopeFilterOptions } from '../hooks/useScopeFilterOptions';
import type {
  ForecastAnomaly,
  ForecastChartHistoryRange,
} from '../types/forecast.types';
import type { ChartGranularity } from '../utils/chartLabels';
import { demandPlanningPageClass } from '../utils/pageShell';

const RUN_STORAGE_KEY = 'dp-forecast-selected-run';

function readStoredRunId(): number | null {
  try {
    const raw = localStorage.getItem(RUN_STORAGE_KEY);
    if (!raw) return null;
    const id = Number.parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function KpiChip({
  label,
  help,
}: {
  label: string;
  help: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 shadow-sm">
      {label}
      <HelpTip>{help}</HelpTip>
    </span>
  );
}

export function ForecastPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<number | null>(readStoredRunId);
  const [entityFilters, setEntityFilters] = useState<EntityScopeFilters>(EMPTY_ENTITY_SCOPE_FILTERS);
  const [historyRange, setHistoryRange] = useState<ForecastChartHistoryRange>('52_weeks');
  const [granularity, setGranularity] = useState<ChartGranularity>('week');
  const [productsPage, setProductsPage] = useState(1);
  const [productsPageSize, setProductsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selectedAlert, setSelectedAlert] = useState<ForecastAnomaly | null>(null);
  const [modalAlert, setModalAlert] = useState<ForecastAnomaly | null>(null);
  const [modalMode, setModalMode] = useState<'approve' | 'dismiss' | null>(null);
  const [inboxBusyId, setInboxBusyId] = useState<number | null>(null);

  const scopeOptions = useScopeFilterOptions(true);
  const entityFiltersKey = entityScopeFiltersKey(entityFilters);

  const runs = useQuery({
    queryKey: ['forecast-runs-list'],
    queryFn: () => forecastService.listRuns(),
  });

  const runRows = runs.data ?? [];

  useEffect(() => {
    if (!runRows.length) return;
    if (selectedRunId != null && runRows.some((r) => r.id === selectedRunId)) return;
    setSelectedRunId(runRows[0].id);
  }, [runRows, selectedRunId]);

  useEffect(() => {
    if (selectedRunId == null) return;
    try {
      localStorage.setItem(RUN_STORAGE_KEY, String(selectedRunId));
    } catch {
      /* ignore */
    }
  }, [selectedRunId]);

  useEffect(() => {
    setProductsPage(1);
  }, [selectedRunId, entityFiltersKey]);

  const insights = useQuery({
    queryKey: ['forecast-run-insights', selectedRunId],
    queryFn: () => forecastService.getInsights(selectedRunId!),
    enabled: selectedRunId != null,
  });

  const anomalies = useQuery({
    queryKey: ['forecast-run-anomalies', selectedRunId, entityFiltersKey],
    queryFn: () => forecastService.getAnomalies(selectedRunId!),
    enabled: selectedRunId != null,
  });

  const invalidateAnomalies = () => {
    void queryClient.invalidateQueries({ queryKey: ['forecast-run-anomalies'] });
    void queryClient.invalidateQueries({ queryKey: ['forecast-anomalies'] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      forecastService.applyAlertRecommendation(id, note),
    onMutate: ({ id }) => setInboxBusyId(id),
    onSettled: () => setInboxBusyId(null),
    onSuccess: () => {
      invalidateAnomalies();
      setModalMode(null);
      setModalAlert(null);
      setSelectedAlert(null);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      forecastService.dismissAlert(id, note),
    onMutate: ({ id }) => setInboxBusyId(id),
    onSettled: () => setInboxBusyId(null),
    onSuccess: () => {
      invalidateAnomalies();
      setModalMode(null);
      setModalAlert(null);
      setSelectedAlert(null);
    },
  });

  const chart = useQuery({
    queryKey: ['forecast-chart-series', selectedRunId, historyRange, granularity, entityFiltersKey],
    queryFn: () =>
      forecastService.getChartSeries(selectedRunId!, {
        historyRange,
        granularity,
        filters: entityFilters,
      }),
    enabled: selectedRunId != null,
  });

  const productSummaries = useQuery({
    queryKey: [
      'forecast-product-summaries',
      selectedRunId,
      entityFiltersKey,
      productsPage,
      productsPageSize,
    ],
    queryFn: () =>
      forecastService.getProductSummaries(selectedRunId!, {
        page: productsPage,
        pageSize: productsPageSize,
        filters: entityFilters,
      }),
    enabled: selectedRunId != null,
  });

  const series = chart.data?.series ?? [];
  const hasChartData = series.some(
    (p) => p.actual_qty != null || p.forecast_qty != null,
  );

  const selectedRun = useMemo(
    () => runRows.find((r) => r.id === selectedRunId),
    [runRows, selectedRunId],
  );

  const productsTotal = productSummaries.data?.count ?? 0;
  const productsTotalPages = Math.max(
    1,
    Math.ceil(productsTotal / productsPageSize),
  );
  const safeProductsPage = Math.min(productsPage, productsTotalPages);

  useEffect(() => {
    if (productsPage > productsTotalPages) {
      setProductsPage(productsTotalPages);
    }
  }, [productsPage, productsTotalPages]);

  const productsPageStart =
    productsTotal === 0 ? 0 : (safeProductsPage - 1) * productsPageSize + 1;
  const productsPageEnd = Math.min(
    safeProductsPage * productsPageSize,
    productsTotal,
  );

  const apiError =
    runs.isError ||
    chart.isError ||
    insights.isError ||
    productSummaries.isError ||
    anomalies.isError;

  return (
    <div className={`${demandPlanningPageClass()} pt-4 md:pt-5`}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-gray-900">{t('forecastPage.title')}</h1>
            <HelpTip>{t('forecastPage.help.page')}</HelpTip>
          </div>
          <p className="mt-1 text-sm text-gray-600">{t('forecastPage.subtitle')}</p>
        </div>
        <Link
          to="/demand-prediction"
          className="text-sm text-stocker-primary hover:underline shrink-0"
        >
          {t('forecastPage.legacyPredictionsLink')}
        </Link>
      </header>

      <DemandPlanningGuide i18nPrefix="forecastPage.guide" />

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs text-amber-950">
        <span>{t('forecastPage.coexistNotice')}</span>
        <HelpTip>{t('forecastPage.help.page')}</HelpTip>
      </div>

      {selectedRunId != null ? (
        <EntityScopeFilterBar
          value={entityFilters}
          onChange={setEntityFilters}
          families={scopeOptions.familyOptions}
          warehouses={scopeOptions.warehouseOptions}
          suppliers={scopeOptions.supplierOptions}
          showWarehouse
        />
      ) : null}

      <SectionCard
        title={t('forecastPage.controlsTitle')}
        icon={<SlidersHorizontal className="h-4 w-4" />}
        help={t('forecastPage.help.controls')}
      >
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-sm min-w-[16rem]">
            <LabelWithHelp
              label={t('forecastPage.forecastRun')}
              help={t('forecastPage.help.forecastRun')}
            />
            <select
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={selectedRunId ?? ''}
              disabled={!runRows.length}
              onChange={(e) => setSelectedRunId(Number(e.target.value))}
            >
              {runRows.length === 0 ? (
                <option value="">{t('forecastPage.noRuns')}</option>
              ) : (
                runRows.map((run) => (
                  <option key={run.id} value={run.id}>
                    #{run.id} · {run.model_version || 'forecast'} · {run.line_count}{' '}
                    {t('forecastPage.lines')}
                    {run.planning_cycle_name ? ` · ${run.planning_cycle_name}` : ''}
                  </option>
                ))
              )}
            </select>
          </label>

          {selectedRun ? (
            <p className="text-xs text-gray-500 pb-2">
              {selectedRun.input_data_from} → {selectedRun.input_data_to}
            </p>
          ) : null}

          <label className="flex flex-col gap-1 text-xs text-gray-600">
            <LabelWithHelp
              label={t('consensus.chartRange')}
              help={t('forecastPage.help.historyRange')}
            />
            <select
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-800"
              value={historyRange}
              onChange={(e) => setHistoryRange(e.target.value as ForecastChartHistoryRange)}
            >
              <option value="52_weeks">{t('consensus.range52Weeks')}</option>
              <option value="ytd">{t('consensus.rangeYtd')}</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-gray-600">
            <LabelWithHelp
              label={t('consensus.chartGranularity')}
              help={t('forecastPage.help.granularity')}
            />
            <select
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-800"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as ChartGranularity)}
            >
              <option value="week">{t('consensus.granularityWeek')}</option>
              <option value="month">{t('consensus.granularityMonth')}</option>
            </select>
          </label>
        </div>
      </SectionCard>

      {insights.data?.run ? (
        <div className="flex flex-wrap gap-2">
          <KpiChip
            label={t('forecastPage.kpiProducts', {
              count: insights.data.run.products_forecasted ?? 0,
            })}
            help={t('forecastPage.help.kpiProducts')}
          />
          <KpiChip
            label={t('forecastPage.kpiLines', {
              count: insights.data.run.lines_created ?? 0,
            })}
            help={t('forecastPage.help.kpiLines')}
          />
          {insights.data.accuracy?.value != null ? (
            <KpiChip
              label={t('forecastPage.kpiAccuracy', {
                value: `${(insights.data.accuracy.value * 100).toFixed(1)}%`,
                metric: insights.data.accuracy.metric_label,
              })}
              help={t('forecastPage.help.kpiAccuracy')}
            />
          ) : null}
        </div>
      ) : null}

      {apiError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('demandPlanning.backendError')}
        </div>
      ) : null}

      {selectedRunId != null ? (
        <ForecastWorkInbox
          data={anomalies.data}
          loading={anomalies.isLoading}
          entityFilters={entityFilters}
          selectedAlertId={selectedAlert?.id ?? null}
          onSelectAlert={setSelectedAlert}
          onRequestApprove={(alert) => {
            setModalAlert(alert);
            setModalMode('approve');
          }}
          onRequestDismiss={(alert) => {
            setModalAlert(alert);
            setModalMode('dismiss');
          }}
          actionBusyId={inboxBusyId}
        />
      ) : null}

      <SectionCard
        title={t('forecastPage.chartTitle')}
        icon={<LineChart className="h-4 w-4" />}
        help={
          <span className="block space-y-2">
            <span>{t('forecastPage.help.chart')}</span>
            <span className="text-gray-500">{t('forecastPage.help.chartCalc')}</span>
          </span>
        }
      >
        {runs.isLoading || chart.isLoading ? (
          <p className="py-16 text-center text-sm text-gray-500">{t('common.loading')}</p>
        ) : runRows.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-500">{t('forecastPage.noRunsHint')}</p>
        ) : !hasChartData ? (
          <p className="py-16 text-center text-sm text-gray-500">{t('forecastPage.noChartData')}</p>
        ) : (
          <>
            <ForecastChart
              data={series}
              currentPeriod={chart.data?.current_period ?? null}
              granularity={chart.data?.granularity ?? granularity}
              height={420}
            />
            <ChartLegendHelp i18nPrefix="forecastPage.chartLegend" />
          </>
        )}
      </SectionCard>

      <SectionCard
        title={t('forecastPage.productsTableTitle')}
        icon={<Table2 className="h-4 w-4" />}
        help={t('forecastPage.help.productsTable')}
        headerExtra={
          !productSummaries.isLoading && productSummaries.data != null ? (
            <span className="text-xs text-gray-500">
              {productsTotal > 0
                ? t('forecastPage.productsTableShowing', {
                    from: productsPageStart,
                    to: productsPageEnd,
                    total: productsTotal,
                  })
                : t('forecastPage.productsTableEmpty')}
            </span>
          ) : null
        }
      >
        {selectedRunId == null || runRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">{t('forecastPage.noRunsHint')}</p>
        ) : (
          <ForecastProductTable
            rows={productSummaries.data?.results ?? []}
            loading={productSummaries.isLoading}
            accuracyMetricLabel={
              productSummaries.data?.accuracy_metric_label ?? 'MAPE'
            }
            page={safeProductsPage}
            pageSize={productsPageSize}
            totalCount={productsTotal}
            onPageChange={setProductsPage}
            onPageSizeChange={(size) => {
              setProductsPageSize(size);
              setProductsPage(1);
            }}
          />
        )}
        <p className="mt-3 flex items-start gap-1 text-xs text-gray-500">
          {t('forecastPage.accuracyUnavailableHint')}
          <HelpTip>{t('forecastPage.help.columnAccuracy')}</HelpTip>
        </p>
      </SectionCard>

      <ForecastInboxActionModal
        open={modalMode != null && modalAlert != null}
        mode={modalMode ?? 'approve'}
        alert={modalAlert}
        busy={inboxBusyId != null}
        onClose={() => {
          setModalMode(null);
          setModalAlert(null);
        }}
        onConfirm={(note) => {
          if (!modalAlert || !modalMode) return;
          if (modalMode === 'approve') {
            approveMutation.mutate({ id: modalAlert.id, note });
          } else {
            dismissMutation.mutate({ id: modalAlert.id, note });
          }
        }}
      />
    </div>
  );
}
